import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { Button, Box, useTheme } from '@mui/material';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import { getAllPeopleForTasksAsync } from '@application/features/people/peopleThunk';
import { useAppDispatch } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { createExternalRelationAndAddToMVPAsync } from '@application/features/mvps/mvpsThunk';
import { toast } from 'react-toastify';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { getEventLanguagesByEventIdAsync } from '@application/features/languages/languagesThunk';
const AddExternalRelationDialog = ({ open, onClose, eventId, onSuccess, isSmallScreen = false }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { selectedCompanyId } = useSelector((state) => state.company);
    const { peopleForTasks } = useSelector((state) => state.people);
    const { eventLanguages } = useSelector((state) => state.languages);
    const { control, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            company: '',
            referredBy: '',
            languageId: undefined
        }
    });
    useEffect(() => {
        if (open && eventId) {
            dispatch(getEventLanguagesByEventIdAsync(eventId));
        }
    }, [open, eventId, dispatch]);
    useEffect(() => {
        if (open && eventLanguages && eventLanguages.length > 0) {
            const primary = eventLanguages.find((l) => l.isPrimary) || eventLanguages[0];
            setValue('languageId', primary.id);
        }
    }, [open, eventLanguages, setValue]);
    const languageOptions = (eventLanguages || []).map((lang) => ({
        label: lang.name,
        value: lang.id,
        icon: lang.flagUrl ? (_jsx(Box, { component: 'img', src: lang.flagUrl, alt: lang.name, sx: { width: 20, height: 15, objectFit: 'cover', borderRadius: 0.5 } })) : undefined
    }));
    const onSubmit = async (values) => {
        try {
            const request = {
                eventId: eventId,
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                email: values.email.trim(),
                phoneNo: values.phone,
                company: values.company ? values.company.trim() : undefined,
                referredBy: values.referredBy ? values.referredBy : undefined,
                preferredLanguageId: values.languageId || undefined
            };
            const response = await dispatch(createExternalRelationAndAddToMVPAsync(request)).unwrap();
            if (response.isSuccess) {
                toast.success(response.message ||
                    t('events.eventDetails.rsvpTracking.externalRelationAddedSuccessfully') ||
                    'External relation created and added to MVP list successfully.');
                reset();
                onClose();
                if (onSuccess) {
                    onSuccess();
                }
            }
            else {
                toast.error(response.message ||
                    t('events.eventDetails.rsvpTracking.failedToAddExternalRelation') ||
                    'Failed to add external relation.');
            }
        }
        catch (error) {
            const errorMessage = error?.error ||
                error?.message ||
                t('events.eventDetails.rsvpTracking.failedToAddExternalRelation') ||
                'Failed to add external relation.';
            toast.error(errorMessage);
        }
    };
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0 && open) {
            dispatch(getAllPeopleForTasksAsync());
        }
    }, [selectedCompanyId, open, dispatch]);
    const referredByOptions = peopleForTasks?.map((people) => ({
        label: `${people.name} (${people.peopleCompany})`,
        value: people.id
    })) ?? [];
    const handleCancel = () => {
        reset();
        onClose();
    };
    // Form content
    const formContent = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', name: 'firstName', label: t('events.eventDetails.rsvpTracking.firstName') || 'First Name', control: control, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.rsvpTracking.firstName') || 'First Name'
                    })
                }, errorMessage: errors.firstName?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'lastName', label: t('events.eventDetails.rsvpTracking.lastName') || 'Last Name', control: control, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.rsvpTracking.lastName') || 'Last Name'
                    })
                }, errorMessage: errors.lastName?.message }), _jsx(PhoneInputMasking, { bg: 'grey8', control: control, label: t('events.eventDetails.rsvpTracking.phone') || 'Phone', name: 'phone', error: errors.phone?.message, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.rsvpTracking.phone') || 'Phone'
                    }),
                    validate: (value) => {
                        // Accepts only numbers, must be at least 8 digits
                        const phoneRegex = /^\d{8,}$/;
                        if (!phoneRegex.test(value || '')) {
                            return t('validations.invalidPhone', {
                                field: t('events.eventDetails.rsvpTracking.phone') || 'Phone'
                            });
                        }
                        return true;
                    }
                } }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'email', label: t('events.eventDetails.rsvpTracking.email') || 'Email', control: control, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.rsvpTracking.email') || 'Email'
                    }),
                    pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('validations.invalidEmail', { field: t('labels.email') || 'Email' }) || 'Invalid email address'
                    }
                }, errorMessage: errors.email?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'company', label: t('events.eventDetails.rsvpTracking.companyOptional') || 'Company (Optional)', control: control, errorMessage: errors.company?.message }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'referredBy', label: t('events.eventDetails.rsvpTracking.referralOptional') || 'Referral (Optional)', options: referredByOptions, error: errors.referredBy?.message }), languageOptions.length > 0 && (_jsx(SingleSelect, { bg: 'grey8', control: control, name: 'languageId', label: t('events.eventDetails.rsvpTracking.invitationLanguage') || 'Invitation Language', options: languageOptions, showSelectedIcon: true, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.rsvpTracking.invitationLanguage') || 'Invitation Language'
                    })
                }, error: errors.languageId?.message }))] }));
    const actionButtons = (_jsxs(Box, { sx: { display: 'flex', gap: 1.5, width: '100%' }, children: [_jsx(Button, { fullWidth: true, onClick: handleCancel, variant: 'outlined', color: 'primary', size: 'large', disabled: isSubmitting, children: t('actions.cancel') || 'Cancel' }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: handleSubmit(onSubmit), disabled: isSubmitting, children: t('actions.save') || 'Save' })] }));
    // Desktop Dialog
    if (!isSmallScreen) {
        return (_jsx(CustomDialog, { backgroundVariant: 'white', open: open, onClose: handleCancel, titleWithIcon: t('events.eventDetails.rsvpTracking.addExternalRelation') || 'Add External Relation', titleIcon: '/assets/images/add-person.png', maxWidth: 'sm', actions: actionButtons, children: _jsx(Box, { p: 1.5, sx: {
                    maxHeight: '65vh',
                    overflowY: 'auto'
                }, children: formContent }) }));
    }
    // Mobile Drawer
    return (_jsx(BDrawer, { anchor: 'bottom', title: t('events.eventDetails.rsvpTracking.addExternalRelation') || 'Add External Relation', open: open, onClose: handleCancel, showCloseButton: true, dialogActions: actionButtons, children: _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: formContent }) }));
};
export default AddExternalRelationDialog;
